import { useState } from 'react';
import type { Lang, NotificationType } from '../data/types';
import { useDashboard, useDashboardActions } from '../data/context';
import { t } from '../i18n/t';
import { waLink, waBookingMessage } from '../lib/whatsapp';
import { Icon, type IconName } from '../components/Icon';

interface NotificationsViewProps {
  lang: Lang;
}

const ICON_FOR: Record<NotificationType, { name: IconName; cls: '' | 'red' | 'terra' }> = {
  new: { name: 'calendar', cls: '' },
  cancel: { name: 'x', cls: 'red' },
  note: { name: 'edit', cls: '' },
  review: { name: 'star', cls: 'terra' },
};

export function NotificationsView({ lang }: NotificationsViewProps) {
  const { notifications, reservations, tenant } = useDashboard();
  const actions = useDashboardActions();
  const [busy, setBusy] = useState(false);
  // Reservations confirmed/cancelled from this list in this session — hides
  // the action buttons immediately and remembers the outcome so the
  // one-tap WhatsApp follow-up can be offered in their place.
  const [handled, setHandled] = useState<Map<string, 'conf' | 'cancel'>>(new Map());
  const [actingOn, setActingOn] = useState<string | null>(null);
  const anyUnread = notifications.some((n) => !n.read);

  const act = async (notifId: string, reservationId: string, status: 'conf' | 'cancel') => {
    if (actingOn) return;
    setActingOn(notifId);
    try {
      await actions.setReservationStatus(reservationId, status);
      setHandled((p) => new Map(p).set(notifId, status));
    } catch (err) {
      console.error('[notifications] reservation update failed:', err);
    } finally {
      setActingOn(null);
    }
  };

  const markAll = async () => {
    if (busy || !anyUnread) return;
    setBusy(true);
    try {
      await actions.markAllNotificationsRead();
    } catch (err) {
      console.error('[notifications] mark-all failed:', err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="view">
      <div className="view-pad">
        <div className="notif-list">
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 10 }}>
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              disabled={busy || !anyUnread}
              onClick={markAll}
            >
              <Icon name="check" size={14} />
              {busy ? '…' : t('markAllRead', lang)}
            </button>
          </div>
          {notifications.map((n) => {
            const { name: iname, cls } = ICON_FOR[n.type] ?? { name: 'bell' as IconName, cls: '' };
            const handleClick = () => {
              if (!n.read) {
                actions.markNotificationRead(n.id, true).catch((err) => {
                  console.error('[notifications] mark-one failed:', err);
                });
              }
            };
            return (
              <div
                key={n.id}
                className={'notif' + (!n.read ? ' unread' : '')}
                onClick={handleClick}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleClick();
                  }
                }}
                style={{ cursor: !n.read ? 'pointer' : 'default' }}
              >
                <div className={'n-ico ' + cls}>
                  <Icon name={iname} size={17} />
                </div>
                <div className="n-body">
                  <div
                    className="n-text"
                    // SAFE: copy comes from our own i18n table and the public
                    // booking endpoint escapes customer-supplied name before
                    // interpolation.
                    dangerouslySetInnerHTML={{ __html: n.text[lang] }}
                  />
                  <div className="n-time">{n.time[lang]}</div>
                  {n.type === 'new' &&
                    // Real data: only while the linked reservation is still
                    // pending. Mock data (no `actionable` field): always show.
                    (n.actionable ?? true) &&
                    !handled.has(n.id) && (
                      <div
                        className="n-act"
                        // Don't bubble click into the row (which would mark read).
                        onClick={(e) => e.stopPropagation()}
                      >
                        <button
                          type="button"
                          className="btn btn-terra btn-sm"
                          disabled={actingOn === n.id}
                          onClick={() =>
                            n.reservationId
                              ? act(n.id, n.reservationId, 'conf')
                              : setHandled((p) => new Map(p).set(n.id, 'conf'))
                          }
                        >
                          <Icon name="check" size={13} />
                          {actingOn === n.id ? '…' : t('confirm', lang)}
                        </button>
                        <button
                          type="button"
                          className="btn btn-ghost btn-sm"
                          disabled={actingOn === n.id}
                          onClick={() =>
                            n.reservationId
                              ? act(n.id, n.reservationId, 'cancel')
                              : setHandled((p) => new Map(p).set(n.id, 'cancel'))
                          }
                        >
                          {t('cancel', lang)}
                        </button>
                      </div>
                    )}
                  {/* Dopo Conferma/Rifiuta: follow-up WhatsApp a un tocco col
                      messaggio già scritto — gratis, dal numero del ristoratore. */}
                  {handled.has(n.id) &&
                    (() => {
                      const outcome = handled.get(n.id)!;
                      const resv = reservations.find((r) => r.id === n.reservationId);
                      if (!resv?.phone) return null;
                      return (
                        <div className="n-act" onClick={(e) => e.stopPropagation()}>
                          <a
                            className="btn btn-ghost btn-sm"
                            href={waLink(resv.phone, waBookingMessage(resv, tenant.name, outcome))}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <Icon name="message" size={13} />
                            {outcome === 'conf'
                              ? lang === 'it'
                                ? 'Invia conferma su WhatsApp'
                                : 'Send WhatsApp confirmation'
                              : lang === 'it'
                                ? 'Avvisa su WhatsApp'
                                : 'Notify on WhatsApp'}
                          </a>
                        </div>
                      );
                    })()}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
